import type { Business } from '@/modules/dashboard/dashboardTypes';
import { Dialog } from '@/components/atoms/Dialog';

import { Select } from '@/components/atoms/Select';
import { useRef, useState } from 'react';
import { Input } from '@/components/atoms/Input';
import { sendAnalytics } from '@/services/analyticsService';
import Loader, { Status } from '@/components/Loader/Loader';
import Button from '@/components/Button/newButtons';
import { Checkmark } from '@/resources/assets/images';
import type { User } from '@/modules/user/userTypes';
import type { EmailSendCustomerReportBadDataProps } from '@/modules/email/emailActions';

export type ReportIncorrectDataModalProps = {
	userDataOptions: Array<string>;
	businessId: Business['_id'];
	userEmail: User['email'];
	isOpen: boolean;
	onCloseModal: VoidFunction;
	isReportIncorrectDataError: boolean;
	isReportIncorrectDataLoading: boolean;
	isReportIncorrectDataSuccess: boolean;
	onReportIncorrectData: (props: EmailSendCustomerReportBadDataProps) => void;
};

export const ReportIncorrectDataModal = ({
	businessId,
	isOpen,
	userEmail,
	userDataOptions,
	isReportIncorrectDataError,
	isReportIncorrectDataLoading,
	isReportIncorrectDataSuccess,
	onReportIncorrectData,
	onCloseModal
}: ReportIncorrectDataModalProps) => {
	const [reportDataSelected, setReportDataSelected] = useState('');

	const additionalDetailsRef = useRef<HTMLInputElement>(null);

	const handleSubmitReport = () => {
		const additionalDetailsValue = additionalDetailsRef.current?.value;

		onReportIncorrectData({
			businessId,
			incorrectData: reportDataSelected,
			userEmail,
			additionalString: additionalDetailsValue
		});

		sendAnalytics({
			type: 'track',
			eventName: 'User attempted bad data report',
			properties: {
				businessId,
				incorrectData: reportDataSelected,
				additionalString: additionalDetailsValue
			}
		});

		if (isReportIncorrectDataSuccess) {
			setTimeout(() => {
				onCloseModal();
			}, 1 * 1000);
		}
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={onCloseModal}>
			<Dialog.Content className="flex flex-col justify-center gap-8">
				<Dialog.Title className="text-2xl text-dm-white">
					Report Incorrect Data
				</Dialog.Title>

				<div className="flex w-full flex-col gap-6">
					<section className="flex flex-col gap-3">
						<header className="text-sm text-dm-white">
							Please indicate the data you wish to report.
						</header>

						<Select.Root
							value={reportDataSelected}
							onValueChange={setReportDataSelected}
						>
							<Select.Trigger>
								<Select.Value placeholder="Select an option from the dropdown" />
							</Select.Trigger>

							<Select.Content>
								{userDataOptions.map((option, index) => (
									<Select.Item
										// This eslint disable is necessary due to be an array of same strings, so the only difference is the element index in the array
										// eslint-disable-next-line react/no-array-index-key
										key={option + index}
										value={option}
									>
										{option}
									</Select.Item>
								))}
							</Select.Content>
						</Select.Root>
					</section>

					<section className="flex flex-col gap-3">
						<header className="text-sm text-dm-white">
							Do you wish to share further details regarding this
							data?
						</header>

						<Input
							color="contrast"
							rounded="md"
							placeholder="Share any additional information or details here"
							ref={additionalDetailsRef}
						/>
					</section>
				</div>

				<Dialog.Footer>
					<Dialog.Close className="font-barlow font-semibold text-orange hover:underline">
						Cancel
					</Dialog.Close>

					<Button onClick={handleSubmitReport} red>
						Report
						{isReportIncorrectDataLoading && (
							<Loader status={Status.LOADING} />
						)}
						{isReportIncorrectDataError && (
							<span className="flex aspect-square h-6 w-6 items-center justify-center rounded-full bg-light-danger text-center text-xs text-dm-white">
								X
							</span>
						)}
						{isReportIncorrectDataSuccess && (
							<span className="flex aspect-square h-6 w-6 items-center justify-center rounded-full bg-green p-px text-center text-xs text-dm-white">
								<Checkmark />
							</span>
						)}
					</Button>
				</Dialog.Footer>
			</Dialog.Content>
		</Dialog.Root>
	);
};
